import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
} from "react";
import { PostsWrapper } from "../../Post/PostsWrapper";
import { PostsListType } from "./PostsList.types";
import { updatePostFromServer } from "../../../DataFetchers/PostDataFetcher";
import { usePostDetailsNavigationActions } from "@infrastructure/Hooks/usePostDetailsNavigationActions";
import { PostDetailsPageActions } from "../../Modals/PostDetailsModal/PostDetailsModal";
import {
    addSubscriber,
    publish,
    removeSubscriber,
} from "@infrastructure/Events/EventBus";
import {
    LATEST_COMMENT_DELETED,
    LATEST_COMMENT_REPORTED,
    POST_DELETED,
    POST_DETAILS_CHANGED,
    POST_REPORTED,
    POST_SUBMITTED,
} from "../../Post/PostForm/events";
import {
    RESET_API_CACHE_REASONS,
    resetIgnoreApiCacheTime,
} from "@infrastructure/Services/ApiUtility";
import { useCursoredInfiniteScroll } from "@infrastructure/Hooks/InfiniteScroll/useCursoredInfiniteScroll";
import { PostType } from "../../Post/Post.types";
import { usePostDetailsModalStore } from "../../Modals/PostDetailsModal/PostDetailsModalStore";
import { usePostUpdatesTracker } from "./PostUpdatesTracker";
import { Box } from "@mui/system";
import { RefreshContentButtonBasic } from "../../RefreshContentButton/RefreshContentButtonBasic";
import { useInView } from "@infrastructure/Hooks/useInView";

const BATCHES_LOADED_THRESHOLD = 4;
const POST_UPDATES_TRACKING_LIMIT = 40;

export const REFRESH_LIST_EVENT = "posts-list-refresh";

const PostsList = (
    {
        enableSharing,
        scrollLoaderOpts,
        postWrapperOpts,
        showLoadMoreButton = false,
        showGroupCoverImage = false,
        listType,
        loadingMoreButtonText = "",
        autoLoadDataAfterClick = undefined,
        setPostsAreLoading,
        onPostCardClick,
        scrollableContainerRef,
        startOfPostsListRef,
        trackPostUpdates = true,
    }: PostsListType,
    ref,
) => {
    const isInfiniteScrollEnabled =
        postWrapperOpts?.isInfiniteScrollEnabled ?? true;

    const {
        updateItem,
        loading,
        items,
        hasNextPage,
        error,
        loadMore,
        resetParams,
        isInitDone,
        deleteItem,
    } = useCursoredInfiniteScroll(
        scrollLoaderOpts.fetcher,
        scrollLoaderOpts.fetchParams,
        scrollLoaderOpts.preparedParams,
        scrollLoaderOpts.maxItemCount,
    );

    const { ref: postListRef, inView } = useInView();
    const posts = useMemo(
        () => [...new Map(items.map((item) => [item.id, item])).values()],
        [items],
    );
    const { hasNewItems } = usePostUpdatesTracker({
        currentPosts: posts.slice(0, POST_UPDATES_TRACKING_LIMIT),
        fetcher: scrollLoaderOpts.fetcher,
        fetchParams: scrollLoaderOpts.fetchParams,
        additionalArguments: scrollLoaderOpts.preparedParams,
        updaterFunction: updateItem,
        deleteItem,
        numberOfItemsToTrack: Math.min(
            posts.length,
            POST_UPDATES_TRACKING_LIMIT,
        ),
        shouldWatch: inView && isInitDone && trackPostUpdates,
    });

    const batchesLoaded = useRef(0);
    useEffect(() => {
        if (!isInitDone) {
            return;
        }
        batchesLoaded.current = batchesLoaded.current + 1;
        setPostsAreLoading(false);
    }, [isInitDone]);

    const shouldShowLoadMoreButton =
        showLoadMoreButton &&
        batchesLoaded.current % BATCHES_LOADED_THRESHOLD === 0;

    const resetPostListStatus = useCallback(() => {
        resetParams(scrollLoaderOpts.fetchParams);
    }, [resetParams, scrollLoaderOpts.fetchParams]);

    const { setIsPostDetailsModalOpen } = usePostDetailsModalStore();
    useEffect(() => {
        const subscriberPostCreate = addSubscriber(POST_SUBMITTED, () => {
            resetIgnoreApiCacheTime(
                RESET_API_CACHE_REASONS.POST_CREATE_OR_UPDATE,
            );
            resetPostListStatus();
        });
        const subscriberPostReport = addSubscriber(
            POST_REPORTED,
            ({
                body: {
                    data: { postId },
                },
            }) => {
                deleteItem("id", postId);
                resetIgnoreApiCacheTime(RESET_API_CACHE_REASONS.POST_REPORT);
                setIsPostDetailsModalOpen(false);
            },
        );
        const subscriberPostDelete = addSubscriber(
            POST_DELETED,
            ({
                body: {
                    data: { postId },
                },
            }) => {
                deleteItem("id", postId);
                resetIgnoreApiCacheTime(RESET_API_CACHE_REASONS.POST_DELETE);
                setIsPostDetailsModalOpen(false);
            },
        );
        return () => {
            removeSubscriber(subscriberPostCreate);
            removeSubscriber(subscriberPostReport);
            removeSubscriber(subscriberPostDelete);
        };
    }, []);

    useImperativeHandle(ref, () => updateItem);
    useEffect(() => {
        if (!loading) {
            resetPostListStatus();
        }
    }, [
        scrollLoaderOpts.fetchParams.sortCriteria,
        scrollLoaderOpts.fetchParams.groupId,
    ]);

    const { navigateToPostWithAction } = usePostDetailsNavigationActions();

    const onCommentButtonClick = (
        e: React.MouseEvent<HTMLElement>,
        postId: number,
    ) => {
        e.stopPropagation();
        navigateToPostWithAction(postId, PostDetailsPageActions.WRITE_COMMENT);
    };

    useEffect(() => {
        const postDetailsChangedSubscriber = addSubscriber(
            POST_DETAILS_CHANGED,
            (eventData) => {
                if (
                    !usePostDetailsModalStore.getState().isPostDetailsModalOpen
                ) {
                    return;
                }
                const data = eventData.body.data as PostType;
                updateItem("id", data.id.toString(), {
                    ...data,
                    pageRelated: scrollLoaderOpts.preparedParams,
                });
            },
        );

        const refreshListSubscriber = addSubscriber(
            REFRESH_LIST_EVENT,
            resetPostListStatus,
        );

        const latestCommentDeletedSubscriber = addSubscriber(
            LATEST_COMMENT_DELETED,
            (eventData) => {
                updatePostFromServer(
                    eventData.body.data.postId,
                    (key, stringPostId, update) => {
                        updateItem(key, stringPostId, update);
                        publish({
                            name: POST_DETAILS_CHANGED,
                            data: update,
                        });
                    },
                );
            },
        );

        const latestCommentReportedSubscriber = addSubscriber(
            LATEST_COMMENT_REPORTED,
            (eventData) => {
                updatePostFromServer(
                    eventData.body.data.postId,
                    (key, stringPostId, update) => {
                        updateItem(key, stringPostId, update);
                        publish({
                            name: POST_DETAILS_CHANGED,
                            data: update,
                        });
                    },
                );
            },
        );

        return () => {
            removeSubscriber(postDetailsChangedSubscriber);
            removeSubscriber(latestCommentDeletedSubscriber);
            removeSubscriber(latestCommentReportedSubscriber);
            removeSubscriber(refreshListSubscriber);
        };
    }, []);

    return (
        <Box ref={postListRef}>
            {inView && hasNewItems && (
                <RefreshContentButtonBasic
                    onClick={() => {
                        resetPostListStatus();
                    }}
                    scrollToRef={startOfPostsListRef}
                    isInsideModal={false}
                />
            )}
            <PostsWrapper
                posts={posts}
                dataLoader={loadMore}
                hasNextPage={hasNextPage}
                isLoading={loading}
                error={error}
                openPostDetailsPage={onCommentButtonClick}
                showShareOption={enableSharing}
                isInfiniteScrollEnabled={isInfiniteScrollEnabled}
                {...postWrapperOpts}
                isInitLoadingDone={isInitDone}
                showLoadMoreButton={shouldShowLoadMoreButton}
                showGroupCoverImage={showGroupCoverImage}
                listType={listType}
                loadingMoreButtonText={loadingMoreButtonText}
                updatePostFromServer={(postId: number) => {
                    updatePostFromServer(
                        postId,
                        (id, stringPostId, newValue) => {
                            updateItem(id, stringPostId, newValue);
                            publish({
                                name: POST_DETAILS_CHANGED,
                                data: newValue,
                            });
                        },
                    );
                }}
                autoLoadDataAfterClick={autoLoadDataAfterClick}
                onPostCardClick={onPostCardClick}
                groupId={scrollLoaderOpts.fetchParams.groupId}
                updatePostComponent={(postData: PostType) => {
                    updateItem("id", postData.id, postData);
                }}
                scrollableContainerRef={scrollableContainerRef}
            />
        </Box>
    );
};

export default forwardRef(PostsList);
